<template>
  <Teleport to="body">
    <Transition name="fade">
      <div v-if="open" class="chess-set-backdrop" @click.self="emit('update:open', false)">
        <div class="chess-set-popover">
          <div class="chess-set-popover-title">{{ localize('gambit.chess_set_popover.title') }}</div>

          <GamChessSetCounter v-model="localCount" is-large :is-disabled="isLoading" />

          <div class="chess-set-popover-status">
            <div class="chess-set-status-row">
              <GamIcon
                :name="playerChessSets > 0 ? GamIconName.CHECK : GamIconName.X_CLOSE"
                :size="GamIconSize.NORMAL"
                :class="playerChessSets > 0 ? 'status-ok' : 'status-none'"
              />
              <span>Players: {{ playerChessSets > 0 ? playerChessSets : 'none' }}</span>
            </div>
            <div class="chess-set-status-row">
              <GamIcon
                :name="spotChessSets > 0 ? GamIconName.CHECK : GamIconName.X_CLOSE"
                :size="GamIconSize.NORMAL"
                :class="spotChessSets > 0 ? 'status-ok' : 'status-none'"
              />
              <span>On spot: {{ spotChessSets > 0 ? spotChessSets : 'unknown' }}</span>
            </div>
          </div>

          <button class="chess-set-popover-confirm" :disabled="isLoading" @click="emit('confirm', localCount)">
            {{ localCount }} {{ localCount === 1 ? 'set' : 'sets' }}
          </button>

          <div class="chess-set-popover-close" @click="emit('update:open', false)">
            <GamIcon :name="GamIconName.X_CLOSE" size="24px" is-current />
          </div>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<script lang="ts" setup>
import { localize } from '@/core/gambits';
import GamChessSetCounter from '@/views/components/GamChessSetCounter.vue';
import GamIcon from '@/views/components/GamIcon.vue';
import { GamIconName, GamIconSize } from '@/views/composables/constants/components/gamIcon.constants';
import { computed, watch } from 'vue';

const props = withDefaults(
  defineProps<{
    open: boolean;
    modelValue: number;
    playerChessSets?: number;
    spotChessSets?: number;
    isLoading?: boolean;
  }>(),
  {
    playerChessSets: 0,
    spotChessSets: 0,
    isLoading: false,
  },
);

const emit = defineEmits<{
  'update:open': [value: boolean];
  'update:modelValue': [value: number];
  confirm: [value: number];
}>();

const localCount = computed({
  get: () => props.modelValue,
  set: (value: number) => emit('update:modelValue', value),
});

watch(
  () => props.open,
  (isOpen) => {
    // No board yet reads as 0. Opening offers 1. A count already chosen stays.
    if (isOpen && props.modelValue < 1) {
      emit('update:modelValue', 1);
    }
  },
);
</script>

<style lang="scss" scoped>
@use '@/ui/css/mixins' as *;
.chess-set-backdrop {
  position: fixed;
  inset: 0;
  z-index: 3000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1.5rem;
  background: rgba(0, 0, 0, 0.5);
  backdrop-filter: blur(4px);
}

.chess-set-popover {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1.25rem;
  padding: var(--spacing-xxl) var(--spacing-xl) 6.5rem;
  border-radius: var(--radius-large);
  border: 1px solid var(--color-white-10);
  background: var(--color-dark-700);
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.5);
  width: 100%;
  max-width: 300px;
}

.chess-set-popover-title {
  font-size: 20px;
  font-weight: 700;
  text-align: center;
  color: var(--color-white);
}

/* Bottom-centred close, matching our confirmation modals. */
.chess-set-popover-close {
  position: absolute;
  bottom: var(--safe-bottom-offset);
  left: 50%;
  transform: translateX(-50%);
  width: 3.5rem;
  height: 3.5rem;
  border-radius: 3.5rem;
  border: 1px solid var(--color-white-10);
  backdrop-filter: blur(8px);
  background-color: rgba(0, 0, 0, 0.2);
  color: var(--color-white-70);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  touch-action: manipulation;
  transition: all 200ms linear;

  @include hover {
    color: var(--color-white);
    border-color: var(--color-white-20);
  }

  @include press($transform: translateX(-50%));
}

.chess-set-popover-status {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 0.5rem;
  font-size: 15px;
  color: var(--color-white-70);
}

.chess-set-status-row {
  display: flex;
  align-items: center;
  gap: 0.25rem;
}

.chess-set-popover-confirm {
  width: 100%;
  padding: 0.875rem 1.5rem;
  border: none;
  border-radius: 2rem;
  background: var(--color-linear-gradient);
  color: #fff;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  touch-action: manipulation;
  transition: opacity 200ms ease;

  @include hover {
    opacity: 0.9;
  }

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
}

.status-ok {
  color: var(--color-green, #4caf50);
}

.status-none {
  color: var(--color-pink);
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 150ms ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>
